import * as React from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useLanguage } from "contexts/language.context";
import ncrService from "services/ncr-service";
import { ncrTextFieldProps } from "../ncrTextValidation";

const normalizeName = (text) =>
  String(text || "").normalize("NFKD").replace(/\p{M}/gu, "").toLocaleLowerCase().trim();

function filterPeople(options, { inputValue }) {
  const search = normalizeName(inputValue);
  const prefixes = search.match(/[\p{L}\p{N}]+/gu) || [];
  return options.filter((option) => {
    const name = normalizeName(option.label);
    if (!search || name.includes(search)) return true;
    if (!prefixes.length) return false;
    const words = name.match(/[\p{L}\p{N}]+/gu) || [];
    let next = 0;
    return prefixes.every((prefix) => {
      while (next < words.length && !words[next].startsWith(prefix)) next++;
      return next++ < words.length;
    });
  });
}

// IDs identify dropdown options only; issued NCRs keep their existing name snapshots.
export default function NcrPeopleFields({
  auditId,
  isOpen,
  disabled,
  value,
  onChange,
  columns = 6,
}) {
  const { label } = useLanguage();
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [retry, setRetry] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState({});
  React.useEffect(() => {
    setSelectedIds({});
    setOptions([]);
    setError("");
    setLoading(false);
    if (!isOpen || !auditId) return;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const [response, users] = await ncrService.getPeople(auditId);
        if (!response.ok)
          throw new Error(
            users?.error?.message ||
              users?.error ||
              label("ncr-users-unavailable") ||
              "Unable to load users",
          );
        if (active)
          setOptions(
            users
              .filter((user) => Number.isInteger(user.id) && user.name)
              .map((user) => ({ id: user.id, label: user.name })),
          );
      } catch (failure) {
        if (active)
          setError(
            failure.message ||
              label("ncr-users-unavailable") ||
              "Unable to load users",
          );
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isOpen, auditId, retry]);

  return (
    <>
      {error ? (
        <Grid item xs={12}>
          <Alert
            severity="error"
            action={
              <Button
                disabled={disabled}
                onClick={() => setRetry((current) => current + 1)}
              >
                {label("ncr-retry") || "Retry"}
              </Button>
            }
          >
            {error}
          </Alert>
        </Grid>
      ) : null}
      {["personInCharge", "teamMember"].map((field) => {
        const matching = options.filter(
          (option) => option.label === value?.[field],
        );
        const selected =
          options.find(
            (option) =>
              option.id === selectedIds[field] &&
              option.label === value?.[field],
          ) || (matching.length === 1 ? matching[0] : null);
        return (
          <Grid item xs={12} md={columns} key={field}>
            <Autocomplete
              freeSolo
              forcePopupIcon
              fullWidth
              openOnFocus
              disabled={disabled}
              loading={loading}
              options={options}
              filterOptions={filterPeople}
              value={selected || value?.[field] || null}
              getOptionLabel={(option) => typeof option === "string" ? option : option.label}
              isOptionEqualToValue={(option, selected) =>
                option.id === selected.id
              }
              onChange={(event, option) => {
                setSelectedIds((current) => ({
                  ...current,
                  [field]: option?.id,
                }));
                onChange((current) => ({
                  ...current,
                  [field]: typeof option === "string" ? option : option?.label || "",
                }));
              }}
              onInputChange={(event, text, reason) => {
                if (reason !== "input" && reason !== "clear") return;
                setSelectedIds((current) => ({ ...current, [field]: undefined }));
                onChange((current) => ({ ...current, [field]: text }));
              }}
              onKeyDown={(event) => {
                // Enter confirms a person, never the entire NCR form.
                if (event.key === "Enter") event.preventDefault();
              }}
              getOptionKey={(option) => option.id}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li {...optionProps} key={option.id}>
                    <Typography variant="body2">{option.label}</Typography>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  {...ncrTextFieldProps(field, value?.[field])}
                  label={
                    field === "personInCharge"
                      ? label("person-in-charge-singular") || "Person in charge"
                      : label("team-member-singular") || "Team member"
                  }
                />
              )}
            />
          </Grid>
        );
      })}
    </>
  );
}
